import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, newIdempotencyKey, poiApi, resourceApi, type AllianceList } from '../lib/api';
import { isTypingTarget } from '../hooks/useGameKeyboardShortcuts';
import { useApp } from '../store';
import { MobileSheetHandle, useMobileSheetExpand } from './MobileSheet';
import { useTranslation } from 'react-i18next';
import { activeLocale } from '../i18n/format';
import { translateApiError } from '../i18n/errors';
import { isInfluencePending, scheduleInfluenceRefresh } from '../lib/influenceRefresh';
import { POI_COLOR, POI_EMOJI, POI_LABEL } from './PoiPanel';

function FlagPoleChip({
  territoryId,
  hasInvestment,
}: {
  territoryId: number;
  hasInvestment: boolean;
}) {
  const { t } = useTranslation(['panels', 'errors']);
  const { user, bumpMapPaint } = useApp();
  const qc = useQueryClient();
  const flags = useQuery({
    queryKey: ['empire-flags-mine', user?.id],
    queryFn: () => api.empireFlagMine(),
    enabled: !!user?.id && !!user.empireFlagUrl,
  });
  const [busy, setBusy] = useState(false);
  const flagUrl = user?.empireFlagUrl || flags.data?.flagUrl;
  const planted = !!flags.data?.poles?.includes(territoryId);
  if (!flagUrl || !hasInvestment) return null;
  return (
    <button
      type="button"
      title={planted ? t('panels:territory.removeFlag') : t('panels:territory.plantFlag')}
      disabled={busy}
      onClick={() => {
        setBusy(true);
        const op = planted
          ? api.clearEmpireFlagPole(territoryId)
          : api.setEmpireFlagPole(territoryId);
        void op
          .then(() => {
            void qc.invalidateQueries({ queryKey: ['empire-flags-mine'] });
            bumpMapPaint();
          })
          .catch((err) => alert(translateApiError(err, t, 'flagImpossible')))
          .finally(() => setBusy(false));
      }}
      className={`rounded border px-1.5 py-0.5 text-[9px] font-bold leading-tight whitespace-nowrap transition disabled:opacity-50 ${
        planted
          ? 'border-[var(--accent)]/50 bg-[var(--accent)]/15 text-[var(--accent)]'
          : 'border-[var(--line)] text-[var(--muted)] hover:border-[var(--accent)] hover:text-[var(--accent)]'
      }`}
    >
      {busy ? '…' : planted ? t('panels:territory.flagPlanted') : t('panels:territory.plant')}
    </button>
  );
}

/** Chips actions territoire (drapeau / voisins / capitale / favori) — compacts pour tenir sur une ligne. */
const territoryActionChip =
  'rounded border px-1.5 py-0.5 text-[9px] font-medium leading-tight whitespace-nowrap transition disabled:opacity-50';

type Owner = {
  userId: number;
  username: string;
  empireName: string | null;
  empireColor: string;
  empireFlagUrl?: string | null;
  percent: number;
  weight: number;
};

type AllianceRelation = 'none' | 'allied' | 'incoming' | 'outgoing';

function allianceBtn(kind: 'primary' | 'ghost' | 'danger' = 'ghost'): string {
  if (kind === 'primary') {
    return 'rounded-lg bg-[var(--accent)] px-2 py-1 text-[11px] font-semibold text-[#1a1408] disabled:opacity-50';
  }
  if (kind === 'danger') {
    return 'rounded-lg border border-[var(--danger)]/50 px-2 py-1 text-[11px] text-[var(--danger)] disabled:opacity-50';
  }
  return 'rounded-lg border border-[var(--line)] px-2 py-1 text-[11px] text-[var(--muted)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-50';
}

export function TerritoryPanel() {
  const { t } = useTranslation(['panels', 'common', 'errors']);
  const { className: sheetExpandClassName } = useMobileSheetExpand('sm');
  const {
    selectedTerritoryId,
    user,
    wallet,
    setPanel,
    openBuildings,
    setSelected,
    setSelectedPoi,
    setWallet,
    setZones,
    setLastReveal,
    conquering,
    setConquering,
    bumpMapPaint,
    claimMapTerritoryColor,
    clearMapTerritoryColor,
    openChatWith,
    neighborHighlightIds,
    setNeighborHighlight,
    shortcuts,
    panel,
  } = useApp();
  const qc = useQueryClient();
  const id = selectedTerritoryId;
  const [pickedUserId, setPickedUserId] = useState<number | null>(null);
  const [allianceError, setAllianceError] = useState<string | null>(null);
  const [, setClockTick] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportSent, setReportSent] = useState(false);
  const [routeMode, setRouteMode] = useState<null | 'pilgrim' | 'scenic'>(null);
  const [routeAnchorId, setRouteAnchorId] = useState<number | null>(null);
  const [routeBusy, setRouteBusy] = useState(false);
  const [routePreview, setRoutePreview] = useState<{
    mode: 'pilgrim' | 'scenic';
    territoryA: number;
    territoryB: number;
    path: number[];
    length: number;
    costs: Record<string, number>;
    durationSeconds?: number;
    isReconsecration?: boolean;
  } | null>(null);

  useEffect(() => {
    setPickedUserId(null);
    setAllianceError(null);
    setReportOpen(false);
    setReportText('');
    setReportError(null);
    setReportSent(false);
  }, [id]);

  useEffect(() => {
    if (!routeMode || routeAnchorId == null || id == null || id === routeAnchorId) {
      return;
    }
    let cancelled = false;
    setRouteBusy(true);
    void (async () => {
      try {
        if (routeMode === 'pilgrim') {
          const res = await api.pilgrimPreview(routeAnchorId, id);
          if (cancelled) return;
          if (!res.ok || !Array.isArray(res.path) || res.path.length < 2) {
            alert(res.error || t('errors:actionImpossible'));
            setRoutePreview(null);
            return;
          }
          setRoutePreview({
            mode: 'pilgrim',
            territoryA: routeAnchorId,
            territoryB: id,
            path: res.path,
            length: res.length ?? res.path.length - 1,
            costs: res.costs ?? {},
            durationSeconds: res.durationSeconds,
            isReconsecration: res.isReconsecration,
          });
          setNeighborHighlight(res.path);
        } else {
          const res = await api.scenicPreview(routeAnchorId, id);
          if (cancelled) return;
          if (!res.ok || !Array.isArray(res.path) || res.path.length < 2) {
            alert(res.error || t('errors:actionImpossible'));
            setRoutePreview(null);
            return;
          }
          setRoutePreview({
            mode: 'scenic',
            territoryA: routeAnchorId,
            territoryB: id,
            path: res.path,
            length: res.length ?? res.path.length - 1,
            costs: res.costs ?? {},
          });
          setNeighborHighlight(res.path);
        }
      } catch (err) {
        if (!cancelled) alert(translateApiError(err, t, 'actionImpossible'));
      } finally {
        if (!cancelled) setRouteBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, routeMode, routeAnchorId, setNeighborHighlight, t]);

  const reportMut = useMutation({
    mutationFn: (body: string) => api.suggestionsSubmit(body, { territoryId: id! }),
    onSuccess: () => {
      setReportText('');
      setReportError(null);
      setReportSent(true);
      window.setTimeout(() => {
        setReportSent(false);
        setReportOpen(false);
      }, 1800);
      void qc.invalidateQueries({ queryKey: ['suggestions-unread'] });
    },
    onError: (err) => {
      setReportError(err instanceof Error ? err.message : t('common:error'));
    },
  });

  const q = useQuery({
    queryKey: ['territory', id],
    queryFn: () => api.territory(id!),
    enabled: id != null,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const poisQ = useQuery({
    queryKey: ['territory-pois', id],
    queryFn: () => poiApi.list({ territoryId: id!, limit: 20 }),
    enabled: id != null,
    staleTime: 60_000,
  });
  const territoryPois = poisQ.data?.features ?? [];

  const territoryGovernorId =
    (q.data?.influence?.governorUserId as number | null | undefined) ?? null;
  const governsTerritory = !!user && territoryGovernorId === user.id;

  const resourceSummaryQ = useQuery({
    queryKey: ['resource-summary'],
    queryFn: () => resourceApi.summary(),
    enabled: !!user && governsTerritory && territoryPois.length > 0,
    staleTime: 60_000,
  });

  const poiHourlyById = useMemo(() => {
    const map = new Map<number, number>();
    if (id == null) return map;
    for (const p of resourceSummaryQ.data?.producingPois ?? []) {
      if (p.territoryId === id) map.set(p.poiId, p.hourlyRate);
    }
    return map;
  }, [resourceSummaryQ.data, id]);

  useEffect(() => {
    const timer = window.setInterval(() => setClockTick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const alliances = useQuery({
    queryKey: ['alliances', user?.id],
    queryFn: () => api.alliances(),
    enabled: !!user,
  });

  const playerCard = useQuery({
    queryKey: ['player-card', pickedUserId],
    queryFn: () => api.playerCard(pickedUserId!),
    enabled: !!user && pickedUserId != null,
  });

  const relationById = useMemo(() => {
    const map = new Map<number, { relation: AllianceRelation; allianceId?: number }>();
    const data = alliances.data;
    if (!data) return map;
    for (const a of data.allies) map.set(a.id, { relation: 'allied', allianceId: a.allianceId });
    for (const a of data.incoming) map.set(a.id, { relation: 'incoming', allianceId: a.allianceId });
    for (const a of data.outgoing) map.set(a.id, { relation: 'outgoing', allianceId: a.allianceId });
    return map;
  }, [alliances.data]);

  const allianceMutation = useMutation({
    mutationFn: (args: {
      action: 'send' | 'accept' | 'decline' | 'cancel' | 'break';
      userId?: number;
      allianceId?: number;
    }) => api.allianceAction(args.action, { userId: args.userId, allianceId: args.allianceId }),
    onSuccess: (data) => {
      setAllianceError(null);
      qc.setQueryData(['alliances', user?.id], data as AllianceList);
      void qc.invalidateQueries({ queryKey: ['player-card'] });
      const pending = isInfluencePending(data as { influencePending?: boolean; recalculationPending?: boolean });
      scheduleInfluenceRefresh(qc, pending, {
        queryKeys: [['territory'], ['geojson']],
        bumpMapPaint,
        delaysMs: pending ? [700, 2200] : undefined,
      });
    },
    onError: (err) => {
      setAllianceError(translateApiError(err, t));
    },
  });

  // Hooks must stay above any conditional return (React #310).
  const martialLawMutation = useMutation({
    mutationFn: () => api.activateMartialLaw(id!, newIdempotencyKey()),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['territory', id] }),
        qc.invalidateQueries({ queryKey: ['geojson'] }),
      ]);
      bumpMapPaint();
    },
    onError: (err) => alert(translateApiError(err, t, 'activationImpossible')),
  });

  const capitalMutation = useMutation({
    mutationFn: async (mode: 'designate' | 'remove') => {
      if (mode === 'designate') return api.designateCulturalCapital(id!);
      return api.removeCulturalCapital(id!);
    },
    onSuccess: (res) => {
      if (!res.ok) {
        alert(res.error || t('errors:actionImpossible'));
        return;
      }
      scheduleInfluenceRefresh(qc, isInfluencePending(res), { bumpMapPaint });
    },
    onError: (err) => alert(translateApiError(err, t, 'actionImpossible')),
  });

  const favoriteMutation = useMutation({
    mutationFn: () => api.toggleFavoriteTerritory(id!),
    onSuccess: (res) => {
      if (!res.ok) {
        alert(res.error || t('errors:actionImpossible'));
        return;
      }
      if (res.favorite) {
        qc.setQueryData(['territory', id], (prev: Record<string, unknown> | undefined) =>
          prev ? { ...prev, favorite: res.favorite } : prev,
        );
      }
      scheduleInfluenceRefresh(qc, isInfluencePending(res), { bumpMapPaint });
    },
    onError: (err) => alert(translateApiError(err, t, 'actionImpossible')),
  });

  /**
   * Après un invest réussi, les voisins land-strong n’ouvrent plus une nouvelle zone :
   * on lève immédiatement le faux ZONE_LIMIT en cache (le refetch confirmera).
   */
  function unlockZoneLimitOnLandNeighbors(investedId: number) {
    type Neighbor = { id: number; type?: string; strong?: boolean };
    type TerritoryCache = {
      investBlockReason?: string | null;
      canInvest?: boolean;
      canExpand?: boolean;
      neighbors?: Neighbor[];
    };
    const self = qc.getQueryData(['territory', investedId]) as TerritoryCache | undefined;
    const rawNeighbors = self?.neighbors ?? q.data?.neighbors;
    const neighbors = Array.isArray(rawNeighbors) ? (rawNeighbors as Neighbor[]) : [];
    const targets = new Set<number>([investedId]);
    for (const n of neighbors) {
      if (n && n.strong && (n.type == null || n.type === 'land')) targets.add(n.id);
    }
    for (const tid of targets) {
      qc.setQueryData(['territory', tid], (prev: TerritoryCache | undefined) => {
        if (!prev || typeof prev !== 'object') return prev;
        if (prev.investBlockReason !== 'ZONE_LIMIT') return prev;
        return { ...prev, canInvest: true, canExpand: true, investBlockReason: null };
      });
    }
  }

  const pauseMutation = useMutation({
    mutationFn: (paused: boolean) => api.setTerritoryPaused(id!, paused),
    onSuccess: (res) => {
      scheduleInfluenceRefresh(qc, isInfluencePending(res), {
        queryKeys: [['territory'], ['geojson'], ['empire-mine'], ['resource-summary'], ['leaderboard']],
        bumpMapPaint,
      });
    },
    onError: (err) => alert(translateApiError(err, t, 'pauseImpossible')),
  });

  const abandonMutation = useMutation({
    mutationFn: () => api.abandonTerritory(id!),
    onSuccess: (res) => {
      if (res.wallet) setWallet(res.wallet);
      if (res.zones) setZones(res.zones);
      if (id != null) clearMapTerritoryColor(id);
      scheduleInfluenceRefresh(qc, isInfluencePending(res), {
        queryKeys: [['territory'], ['geojson'], ['empire-mine'], ['resource-summary'], ['leaderboard']],
        bumpMapPaint,
      });
    },
    onError: (err) => alert(translateApiError(err, t, 'abandonImpossible')),
  });

  const recoverMutation = useMutation({
    mutationFn: () => api.recoverRecentInvestments(id!),
    onSuccess: (res) => {
      if (res.wallet) setWallet(res.wallet);
      if (res.zones) setZones(res.zones);
      const emptied =
        (res as { emptied?: boolean }).emptied === true ||
        Number(
          ((res as { influence?: { viewer?: { directPoints?: number } } }).influence?.viewer
            ?.directPoints ?? 0),
        ) <= 0;
      if (emptied && id != null) clearMapTerritoryColor(id);
      scheduleInfluenceRefresh(qc, isInfluencePending(res), {
        queryKeys: [['territory'], ['geojson'], ['empire-mine'], ['resource-summary'], ['leaderboard']],
        bumpMapPaint,
      });
    },
    onError: (err) => alert(translateApiError(err, t, 'recoverImpossible')),
  });

  // Dérivés avant tout return conditionnel (hooks stables — Empire→territoire
  // monte souvent le panneau avec selectedTerritoryId encore null un tick).
  const terr = q.data?.territory as Record<string, unknown> | undefined;
  const inf = q.data?.influence;
  const viewer = inf?.viewer as Record<string, unknown> | null | undefined;
  const canInvest = !!q.data?.canInvest;
  const investBlockReason = q.data?.investBlockReason as string | null | undefined;
  const allyGovernor = q.data?.allyGovernor ?? null;
  const blockedByAlly = investBlockReason === 'ALLY_GOVERNOR' && !!allyGovernor;
  const blockedByZones = investBlockReason === 'ZONE_LIMIT';
  const blockedByAdjacentGov = investBlockReason === 'NEED_ADJACENT_GOVERNOR';
  const zonesInfo = q.data?.zones as
    | { count: number; max: number; graceActive?: boolean; graceEndsAtMs?: number }
    | null
    | undefined;
  const graceActive =
    !!zonesInfo?.graceActive && (zonesInfo.graceEndsAtMs ?? 0) > Date.now();
  const martialLaw = q.data?.martialLaw;
  const isGovernor = !!user && martialLaw?.governorUserId === user.id;
  const foreignGovernor =
    !!user &&
    martialLaw?.governorUserId != null &&
    martialLaw.governorUserId !== user.id;
  const blockedByMartialLaw = (investBlockReason === 'MARTIAL_LAW' || investBlockReason === 'MARTIAL_LAW_ACTIVE') && !isGovernor;
  const noCharges = !!wallet && wallet.charges < 1;
  const investDisabled = conquering || noCharges || !canInvest;
  const investTitle = noCharges
    ? t('panels:territory.noCharges')
    : blockedByZones && zonesInfo
      ? t('panels:territory.zoneWouldOpen', { count: zonesInfo.count, max: zonesInfo.max })
      : blockedByMartialLaw
        ? t('panels:territory.martialActive')
        : blockedByAlly
          ? t('panels:territory.allyGovernor')
          : blockedByAdjacentGov
            ? t('panels:territory.needAdjacentGovernor')
            : investBlockReason === 'TERRITORY_PAUSED'
              ? t('panels:territory.pausedHint')
              : undefined;
  const martialRemaining = martialLaw?.active && martialLaw.expiresAt
    ? Math.max(0, Math.ceil((new Date(martialLaw.expiresAt.replace(' ', 'T')).getTime() - Date.now()) / 1000))
    : Math.max(0, martialLaw?.remainingSeconds ?? 0);
  const martialCountdown = [Math.floor(martialRemaining / 3600), Math.floor((martialRemaining % 3600) / 60), martialRemaining % 60]
    .map((value) => String(value).padStart(2, '0')).join(':');
  const viewerPaused = !!q.data?.viewerPaused;
  const hasInvestment = Number(viewer?.directPoints ?? 0) > 0;
  const owners = (Array.isArray(inf?.owners) ? (inf.owners as Owner[]) : []).slice(0, 5);
  const recoverInfo = q.data?.recover as
    | { available?: boolean; charges?: number; expiresAt?: string | null }
    | null
    | undefined;
  const recoverExpiresMs = (() => {
    const raw = recoverInfo?.expiresAt;
    if (!raw) return 0;
    const iso = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const tms = new Date(iso).getTime();
    return Number.isFinite(tms) ? tms : 0;
  })();
  const recoverWindowOpen = recoverExpiresMs > Date.now();
  const recoverCharges = Number(recoverInfo?.charges ?? 0);
  const recoverRemainingSec = recoverWindowOpen
    ? Math.max(0, Math.ceil((recoverExpiresMs - Date.now()) / 1000))
    : 0;
  const showRecoverInsteadOfAbandon = hasInvestment && recoverWindowOpen;

  async function invest() {
    if (!user || !id) {
      setPanel('auth');
      return;
    }
    if (user.needsEmpireName) {
      setPanel('profile');
      return;
    }
    // Verrou court : empêche le double-clic pendant le round-trip HTTP (désormais rapide).
    // Le recalcul d’influence tourne côté serveur après la réponse — le bouton redevient dispo tout de suite.
    setConquering(true);
    try {
      const res = (await api.invest(id, newIdempotencyKey())) as {
        ok?: boolean;
        error?: string;
        pointsGained?: number;
        investmentCount?: number;
        directPoints?: number;
        offensive?: boolean;
        firstInvestment?: boolean;
        territory?: { name: string };
        wallet?: import('../lib/api').Wallet;
        zones?: import('../lib/api').ZonesStatus;
        influence?: {
          viewer?: Record<string, unknown> | null;
          governorUserId?: number | null;
          [key: string]: unknown;
        };
        influencePending?: boolean;
        recalculationPending?: boolean;
      };
      if (!res.ok) throw new Error(res.error || t('panels:territory.investImpossible'));
      if (res.wallet) setWallet(res.wallet);
      if (res.zones) setZones(res.zones);
      const gained = Number(res.pointsGained ?? 0);
      setLastReveal({ name: (res.territory?.name as string) || String(terr?.name ?? ''), points: gained });
      // MAJ immédiate du poids / compteurs — ne jamais remplacer influence
      // entière par un payload partiel (sinon owners non-tableau → crash rendu).
      qc.setQueryData(['territory', id], (prev: Record<string, unknown> | undefined) => {
        if (!prev || typeof prev !== 'object') return prev;
        try {
          const prevInf =
            prev.influence && typeof prev.influence === 'object'
              ? (prev.influence as Record<string, unknown>)
              : {};
          const apiInf =
            res.influence && typeof res.influence === 'object'
              ? (res.influence as Record<string, unknown>)
              : {};
          const prevOwners = Array.isArray(prevInf.owners) ? prevInf.owners : [];
          const apiOwners = Array.isArray(apiInf.owners) ? apiInf.owners : null;
          const prevViewer = (prevInf.viewer ?? null) as Record<string, unknown> | null;
          const apiViewer = (apiInf.viewer ?? null) as Record<string, unknown> | null;
          const nextCount = Number(res.investmentCount ?? apiViewer?.investmentCount ?? prevViewer?.investmentCount ?? 0);
          const schedule = [10, 7, 5, 3, 2];
          const nextPts = nextCount < schedule.length ? schedule[nextCount]! : 2;
          const viewer = {
            ...(prevViewer ?? {}),
            ...(apiViewer ?? {}),
            directPoints: Number(res.directPoints ?? apiViewer?.directPoints ?? prevViewer?.directPoints ?? gained),
            investmentCount: nextCount,
            nextPoints: Number(apiViewer?.nextPoints ?? nextPts),
            weight: Number(
              apiViewer?.weight ?? Number(prevViewer?.weight ?? 0) + gained,
            ),
          };
          const nextInf: Record<string, unknown> = {
            ...prevInf,
            ...apiInf,
            owners: apiOwners ?? prevOwners,
            viewer,
          };
          if (!res.offensive) {
            nextInf.governorUserId = user.id;
            nextInf.contested = false;
          }
          return { ...prev, influence: nextInf };
        } catch (patchErr) {
          console.error('invest territory cache patch failed', patchErr);
          return prev;
        }
      });
      // Les voisins déjà ouverts en cache gardaient ZONE_LIMIT pendant ~15 s (staleTime).
      unlockZoneLimitOnLandNeighbors(id);
      // Sans adversaire actif : couleur immédiate (feature-state + leadership provisoire API).
      if (!res.offensive && user.empireColor) {
        claimMapTerritoryColor(id, user.empireColor, user.id);
      } else {
        bumpMapPaint();
      }
      // Libérer le bouton dès la réponse HTTP (avant le refresh carte/influence exacte).
      setConquering(false);
      scheduleInfluenceRefresh(qc, isInfluencePending(res), { bumpMapPaint });
    } catch (err) {
      alert(translateApiError(err, t));
      setConquering(false);
    }
  }

  // Raccourci investir (touche configurable) sur le territoire affiché.
  // Doit rester AVANT le return null (sinon crash React #310 Empire→territoire).
  useEffect(() => {
    const canShortcutInvest =
      id != null &&
      pickedUserId == null &&
      !!terr &&
      !terr.isAggregate &&
      !!user &&
      !viewerPaused &&
      !blockedByAlly &&
      !blockedByAdjacentGov &&
      !blockedByMartialLaw &&
      !investDisabled;

    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      if (isTypingTarget(e.target)) return;
      if (panel === 'shortcuts') return;
      if (e.key.toLowerCase() !== shortcuts.invest) return;
      if (!canShortcutInvest) return;
      e.preventDefault();
      void invest();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // invest() ferme sur l’état courant du panneau ; on se rattache aux flags d’éligibilité.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- volontaire : miroir du bouton Investir
  }, [
    terr,
    user,
    viewerPaused,
    blockedByAlly,
    blockedByAdjacentGov,
    blockedByMartialLaw,
    investDisabled,
    conquering,
    id,
    shortcuts.invest,
    panel,
    pickedUserId,
  ]);

  if (id == null) return null;

  function openPlayer(owner: Owner) {
    if (!user) {
      setPanel('auth');
      return;
    }
    if (owner.userId === user.id) return;
    setAllianceError(null);
    setReportOpen(false);
    setPickedUserId(owner.userId);
  }

  function closePlayerCard() {
    setPickedUserId(null);
    setAllianceError(null);
  }

  function renderAllianceActions(owner: Owner) {
    const known = relationById.get(owner.userId);
    const relation = known?.relation ?? 'none';
    const allianceId = known?.allianceId;
    const busy = allianceMutation.isPending;

    if (relation === 'allied' && allianceId) {
      return (
        <>
          <span className="text-[11px] text-[var(--accent)]">{t('panels:territory.ally')}</span>
          <button
            type="button"
            disabled={busy}
            className={allianceBtn('danger')}
            onClick={() => allianceMutation.mutate({ action: 'break', allianceId })}
          >
            {t('panels:alliances.break')}
          </button>
        </>
      );
    }
    if (relation === 'incoming' && allianceId) {
      return (
        <>
          <button
            type="button"
            disabled={busy}
            className={allianceBtn('primary')}
            onClick={() => allianceMutation.mutate({ action: 'accept', allianceId })}
          >
            {t('panels:alliances.accept')}
          </button>
          <button
            type="button"
            disabled={busy}
            className={allianceBtn()}
            onClick={() => allianceMutation.mutate({ action: 'decline', allianceId })}
          >
            {t('panels:alliances.refuse')}
          </button>
        </>
      );
    }
    if (relation === 'outgoing' && allianceId) {
      return (
        <>
          <span className="text-[11px] text-[var(--muted)]">{t('panels:territory.requestSent')}</span>
          <button
            type="button"
            disabled={busy}
            className={allianceBtn()}
            onClick={() => allianceMutation.mutate({ action: 'cancel', allianceId })}
          >
            {t('panels:alliances.cancel')}
          </button>
        </>
      );
    }
    return (
      <button
        type="button"
        disabled={busy}
        className={allianceBtn('primary')}
        onClick={() => allianceMutation.mutate({ action: 'send', userId: owner.userId })}
      >
        {t('panels:territory.requestAlliance')}
      </button>
    );
  }

  if (pickedUserId != null) {
    const ownerFromList = owners.find((o) => o.userId === pickedUserId);
    const p = playerCard.data?.player;
    const owner: Owner = ownerFromList ?? {
      userId: pickedUserId,
      username: p?.username ?? '…',
      empireName: p?.empireName ?? null,
      empireColor: p?.empireColor ?? '#3D8BFF',
      empireFlagUrl: p?.empireFlagUrl ?? null,
      percent: 0,
      weight: 0,
    };
    const flagUrl = p?.empireFlagUrl || owner.empireFlagUrl;
    const empireName = p?.empireName || owner.empireName;
    const color = p?.empireColor || owner.empireColor;
    const username = p?.username || owner.username;
    const memberSince = p?.createdAt
      ? (() => {
          const d = new Date(
            p.createdAt.includes('T') ? p.createdAt : p.createdAt.replace(' ', 'T'),
          );
          if (Number.isNaN(d.getTime())) return null;
          return d.toLocaleDateString(activeLocale(), { month: 'short', year: 'numeric' });
        })()
      : null;

    return (
      <aside className={`absolute inset-x-2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] z-30 flex max-h-[min(44dvh,21rem)] flex-col overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--bg-panel)]/95 shadow-2xl backdrop-blur-xl sm:inset-x-auto sm:right-4 sm:top-24 sm:bottom-4 sm:max-h-none sm:w-[24rem] sm:rounded-3xl sm:bg-[var(--bg-panel)] ${sheetExpandClassName}`}>
      <MobileSheetHandle />
        <div className="flex shrink-0 justify-center pb-1 pt-2 sm:hidden" aria-hidden="true">
          <span className="h-1 w-9 rounded-full bg-white/25" />
        </div>
        <div
          className="h-1.5 w-full shrink-0"
          style={{ background: color }}
          aria-hidden
        />
        <div className="min-h-0 flex-1 overflow-auto px-3.5 pb-3 pt-3 sm:p-5">
          <div className="mb-3 flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-start gap-3">
              {flagUrl ? (
                <img
                  src={flagUrl}
                  alt=""
                  className="h-12 w-[4.5rem] shrink-0 rounded-md border border-[var(--line)] object-cover shadow-sm sm:h-14 sm:w-[5.25rem]"
                />
              ) : (
                <span
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-[var(--line)] text-lg font-bold text-white/90 sm:h-14 sm:w-14"
                  style={{ background: color }}
                >
                  {(empireName || username).slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <h2 className="truncate font-[family-name:var(--font-display)] text-xl leading-tight text-[var(--ink)] sm:text-2xl">
                  {empireName || username}
                </h2>
                <p className="truncate text-xs text-[var(--muted)] sm:text-sm">
                  @{username}
                  {memberSince
                    ? ` · ${t('panels:territory.memberSince', { date: memberSince })}`
                    : ''}
                </p>
              </div>
            </div>
            <button
              type="button"
              data-panel-close
              className="shrink-0 rounded-lg px-1.5 py-0.5 text-xs text-[var(--muted)] hover:text-[var(--ink)] sm:text-sm"
              onClick={closePlayerCard}
            >
              {t('common:close')}
            </button>
          </div>

          {playerCard.isLoading && (
            <p className="text-sm text-[var(--muted)]">{t('common:loading')}</p>
          )}

          {p && (
            <dl className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl border border-[var(--line)] bg-[#0b1219]/50 px-2 py-2.5">
                <dt className="text-[9px] uppercase tracking-wide text-[var(--muted)]">
                  {t('panels:territory.statGoverned')}
                </dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-[var(--ink)]">
                  {p.governed}
                </dd>
              </div>
              <div className="rounded-xl border border-[var(--line)] bg-[#0b1219]/50 px-2 py-2.5">
                <dt className="text-[9px] uppercase tracking-wide text-[var(--muted)]">
                  {t('panels:territory.statTerritories')}
                </dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-[var(--ink)]">
                  {p.territories}
                </dd>
              </div>
              <div className="rounded-xl border border-[var(--line)] bg-[#0b1219]/50 px-2 py-2.5">
                <dt className="text-[9px] uppercase tracking-wide text-[var(--muted)]">
                  {t('panels:territory.statAllies')}
                </dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-[var(--ink)]">
                  {p.alliesCount}
                </dd>
              </div>
            </dl>
          )}

          {p?.topTerritory && (
            <p className="mt-3 text-xs leading-relaxed text-[var(--muted)]">
              {t('panels:territory.stronghold', {
                name: p.topTerritory.name,
                weight: p.topTerritory.weight,
              })}
            </p>
          )}

          {allianceError && (
            <p className="mt-3 text-xs text-[var(--danger)]">{allianceError}</p>
          )}
        </div>

        <div className="shrink-0 space-y-2 border-t border-[var(--line)] px-3.5 py-3 sm:px-5 sm:py-4">
          <div className="flex flex-wrap items-center gap-1.5">
            {renderAllianceActions(owner)}
          </div>
          <button
            type="button"
            onClick={() => openChatWith(owner.userId)}
            className="w-full rounded-xl border border-[var(--line)] px-3 py-2 text-sm font-semibold text-[var(--ink)] transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
          >
            {t('panels:territory.chat')}
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`absolute inset-x-2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] z-30 flex max-h-[min(44dvh,21rem)] flex-col overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--bg-panel)]/95 shadow-2xl backdrop-blur-xl sm:inset-x-auto sm:right-4 sm:top-24 sm:bottom-4 sm:max-h-none sm:w-[24rem] sm:rounded-3xl sm:bg-[var(--bg-panel)] ${sheetExpandClassName}`}>
      <MobileSheetHandle />
      <div className="flex shrink-0 justify-center pb-1 pt-2 sm:hidden" aria-hidden="true">
        <span className="h-1 w-9 rounded-full bg-white/25" />
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-3.5 pb-2 pt-1 sm:p-5 sm:pb-3">
        <div className="mb-2 sm:mb-3">
          <div className="flex items-start justify-between gap-2 sm:gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
                {String(terr?.type ?? '…')}
                {terr?.parentName ? ` · ${String(terr.parentName)}` : ''}
              </p>
              <div className="flex items-center gap-2">
                {(() => {
                  const govId = inf?.governorUserId as number | null | undefined;
                  const gov = govId
                    ? owners.find((o) => o.userId === govId)
                    : undefined;
                  if (!gov?.empireFlagUrl) return null;
                  return (
                    <img
                      src={gov.empireFlagUrl}
                      alt=""
                      title={gov.empireName || gov.username}
                      className="h-5 w-[1.875rem] shrink-0 rounded-sm border border-[var(--line)] object-cover shadow-sm sm:h-6 sm:w-9"
                    />
                  );
                })()}
                <h2 className="min-w-0 font-[family-name:var(--font-display)] text-lg leading-tight text-[var(--ink)] sm:text-2xl">
                  {String(terr?.name ?? t('panels:territory.loadingName'))}
                </h2>
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1 sm:pt-0.5">
              <button data-panel-close 
                type="button"
                className="rounded-lg px-1.5 py-0.5 text-xs text-[var(--muted)] hover:text-[var(--ink)] sm:text-sm"
                onClick={() => {
                  setPanel('none');
                  setSelected(null);
                }}
              >
                {t('common:close')}
              </button>
              <button
                type="button"
                title={t('panels:territory.reportBug')}
                aria-label={t('panels:territory.reportBug')}
                className="flex h-5 w-5 items-center justify-center rounded-full border border-[var(--line)] text-[11px] font-bold leading-none text-[var(--muted)] transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
                onClick={() => {
                  if (!user) {
                    setPanel('auth');
                    return;
                  }
                  setReportOpen((v) => !v);
                  setReportError(null);
                  setReportSent(false);
                }}
              >
                !
              </button>
            </div>
          </div>
          {reportOpen && id != null && (
            <div className="mt-2 rounded-xl border border-[var(--line)] bg-[#0b1219]/80 p-3 shadow-lg">
              <p className="text-[11px] font-semibold text-[var(--accent)]">
                {t('panels:territory.reportTitle')}
              </p>
              <p className="mt-1 text-[10px] leading-relaxed text-[var(--muted)]">
                {t('panels:territory.reportHint')}
              </p>
              <textarea
                value={reportText}
                onChange={(e) => {
                  setReportText(e.target.value.slice(0, 2000));
                  if (reportError) setReportError(null);
                }}
                rows={3}
                placeholder={t('panels:territory.reportPlaceholder')}
                className="mt-2 w-full resize-y rounded-lg border border-[var(--line)] bg-[#0b1219] px-2.5 py-2 text-xs leading-relaxed text-[var(--ink)] outline-none placeholder:text-[var(--muted)]/60 focus:border-[var(--accent)]"
              />
              <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px] text-[var(--muted)]">
                <span>
                  {reportText.trim().length < 8
                    ? t('panels:territory.reportMin')
                    : `${reportText.length}/2000`}
                </span>
              </div>
              {reportError && (
                <p className="mt-1.5 text-[10px] text-[var(--danger)]">{reportError}</p>
              )}
              {reportSent && (
                <p className="mt-1.5 text-[10px] text-[var(--ok)]">
                  {t('panels:territory.reportSent')}
                </p>
              )}
              <button
                type="button"
                disabled={
                  reportText.trim().length < 8 ||
                  reportText.length > 2000 ||
                  reportMut.isPending ||
                  reportSent
                }
                onClick={() => reportMut.mutate(reportText.trim())}
                className="mt-2 w-full rounded-lg bg-[var(--accent)] px-3 py-1.5 text-[11px] font-semibold text-[#1a1408] disabled:opacity-45"
              >
                {reportMut.isPending
                  ? t('panels:territory.reportSending')
                  : t('panels:territory.reportSend')}
              </button>
            </div>
          )}
          <div className="mt-1.5 flex flex-nowrap items-center gap-1 overflow-x-auto sm:mt-2">
            {id != null && (
              <FlagPoleChip territoryId={id} hasInvestment={hasInvestment && !viewerPaused} />
            )}
            {(() => {
              const neighbors = q.data?.neighbors ?? [];
              const lit = neighborHighlightIds.length > 0;
              return (
                <button
                  type="button"
                  title={
                    neighbors.length === 0
                      ? t('panels:territory.noNeighbors')
                      : lit
                        ? t('panels:territory.hideNeighbors')
                        : t('panels:territory.showNeighbors')
                  }
                  disabled={neighbors.length === 0}
                  aria-pressed={lit}
                  onClick={() => {
                    if (lit) setNeighborHighlight([]);
                    else setNeighborHighlight(neighbors.map((n) => n.id));
                  }}
                  className={`${territoryActionChip} disabled:cursor-not-allowed disabled:opacity-40 ${
                    lit
                      ? 'border-sky-300/60 bg-sky-400/15 text-sky-200'
                      : 'border-[var(--line)] text-[var(--muted)] hover:border-sky-300/50 hover:text-sky-200'
                  }`}
                >
                  {lit ? t('panels:territory.neighborsOn') : t('panels:territory.neighbors')}
                </button>
              );
            })()}
            {(() => {
              const cap = q.data?.culturalCapital;
              if (!user || !cap || (cap.slots ?? 0) < 1) return null;
              if (cap.isCapital) {
                return (
                  <button
                    type="button"
                    title={t('panels:territory.removeCapital')}
                    disabled={capitalMutation.isPending}
                    onClick={() => capitalMutation.mutate('remove')}
                    className={`${territoryActionChip} border-fuchsia-300/40 bg-fuchsia-500/10 text-fuchsia-100/90 hover:bg-fuchsia-500/20`}
                  >
                    {t('panels:territory.capitalActive')}
                  </button>
                );
              }
              if (!cap.canDesignate) return null;
              return (
                <button
                  type="button"
                  title={t('panels:territory.setCapital', { used: cap.used, slots: cap.slots })}
                  disabled={capitalMutation.isPending}
                  onClick={() => capitalMutation.mutate('designate')}
                  className={`${territoryActionChip} border-[var(--line)] text-[var(--muted)] hover:border-fuchsia-300/40 hover:text-fuchsia-100/90`}
                >
                  {t('panels:territory.capital')}
                </button>
              );
            })()}
            {(() => {
              const fav = q.data?.favorite;
              if (!user || !fav?.unlocked) return null;
              if (fav.isFavorite) {
                const lockMs = fav.lockedUntil
                  ? Math.max(
                      0,
                      new Date(fav.lockedUntil.replace(' ', 'T')).getTime() - Date.now(),
                    )
                  : 0;
                const locked = lockMs > 0 || !fav.canRemove;
                const lockHours = Math.max(1, Math.ceil(lockMs / 3_600_000));
                return (
                  <button
                    type="button"
                    title={
                      locked
                        ? t('panels:territory.favoriteLocked', {
                            hours: lockHours,
                            used: fav.used,
                            slots: fav.slots,
                          })
                        : t('panels:territory.unfavorite')
                    }
                    disabled={favoriteMutation.isPending || locked}
                    onClick={() => favoriteMutation.mutate()}
                    className={`${territoryActionChip} border-amber-300/40 bg-amber-500/10 text-amber-100/90 hover:bg-amber-500/20`}
                  >
                    {t('panels:territory.favoriteActive', { used: fav.used, slots: fav.slots })}
                  </button>
                );
              }
              if (!fav.canAdd && fav.used >= fav.slots) {
                return (
                  <button
                    type="button"
                    title={t('panels:territory.favoriteFull', { slots: fav.slots })}
                    disabled
                    className={`${territoryActionChip} border-[var(--line)] text-[var(--muted)] opacity-50`}
                  >
                    {t('panels:territory.favorite', { used: fav.used, slots: fav.slots })}
                  </button>
                );
              }
              if (!fav.canToggle) return null;
              return (
                <button
                  type="button"
                  title={t('panels:territory.setFavorite', {
                    used: fav.used,
                    slots: fav.slots,
                    hours: fav.lockHours,
                  })}
                  disabled={favoriteMutation.isPending}
                  onClick={() => favoriteMutation.mutate()}
                  className={`${territoryActionChip} border-[var(--line)] text-[var(--muted)] hover:border-amber-300/40 hover:text-amber-100/90`}
                >
                  {t('panels:territory.favorite', { used: fav.used, slots: fav.slots })}
                </button>
              );
            })()}
            {(() => {
              const pilgrim = q.data?.pilgrimPaths as
                | { unlocked?: boolean; slots?: number; used?: number }
                | null
                | undefined;
              if (!user || !pilgrim?.unlocked) return null;
              const active = routeMode === 'pilgrim';
              return (
                <button
                  type="button"
                  title={t('panels:territory.pilgrimHint', {
                    used: pilgrim.used ?? 0,
                    slots: pilgrim.slots ?? 0,
                  })}
                  disabled={routeBusy}
                  aria-pressed={active}
                  onClick={() => {
                    if (active) {
                      setRouteMode(null);
                      setRouteAnchorId(null);
                      setRoutePreview(null);
                      setNeighborHighlight([]);
                      return;
                    }
                    if (id == null) return;
                    setRouteMode('pilgrim');
                    setRouteAnchorId(id);
                    setRoutePreview(null);
                    setNeighborHighlight([id]);
                  }}
                  className={`${territoryActionChip} ${
                    active
                      ? 'border-violet-300/50 bg-violet-500/15 text-violet-100'
                      : 'border-[var(--line)] text-[var(--muted)] hover:border-violet-300/40 hover:text-violet-100'
                  }`}
                >
                  {t('panels:territory.pilgrim', {
                    used: pilgrim.used ?? 0,
                    slots: pilgrim.slots ?? 0,
                  })}
                </button>
              );
            })()}
            {(() => {
              const scenic = q.data?.scenicRoutes as
                | { unlocked?: boolean; slots?: number; used?: number }
                | null
                | undefined;
              if (!user || !scenic?.unlocked) return null;
              const active = routeMode === 'scenic';
              return (
                <button
                  type="button"
                  title={t('panels:territory.scenicHint', {
                    used: scenic.used ?? 0,
                    slots: scenic.slots ?? 0,
                  })}
                  disabled={routeBusy}
                  aria-pressed={active}
                  onClick={() => {
                    if (active) {
                      setRouteMode(null);
                      setRouteAnchorId(null);
                      setRoutePreview(null);
                      setNeighborHighlight([]);
                      return;
                    }
                    if (id == null) return;
                    setRouteMode('scenic');
                    setRouteAnchorId(id);
                    setRoutePreview(null);
                    setNeighborHighlight([id]);
                  }}
                  className={`${territoryActionChip} ${
                    active
                      ? 'border-teal-300/50 bg-teal-500/15 text-teal-100'
                      : 'border-[var(--line)] text-[var(--muted)] hover:border-teal-300/40 hover:text-teal-100'
                  }`}
                >
                  {t('panels:territory.scenic', {
                    used: scenic.used ?? 0,
                    slots: scenic.slots ?? 0,
                  })}
                </button>
              );
            })()}
          </div>
          {routeMode && routeAnchorId != null && !routePreview && (
            <p className="mt-1.5 text-[10px] text-[var(--muted)]">
              {t('panels:territory.routePickSecond')}
            </p>
          )}
          {routePreview && (
            <div className="mt-2 rounded-lg border border-[var(--line)] bg-black/20 p-2 text-[11px]">
              <p className="font-medium text-[var(--text)]">
                {routePreview.mode === 'pilgrim'
                  ? t('panels:territory.pilgrimPreview', { length: routePreview.length })
                  : t('panels:territory.scenicPreview', { length: routePreview.length })}
              </p>
              <p className="mt-1 text-[var(--muted)]">
                {Object.entries(routePreview.costs)
                  .map(([res, amt]) => `${amt} ${res}`)
                  .join(' · ')}
                {routePreview.durationSeconds
                  ? ` · ${Math.round(routePreview.durationSeconds / 3600)} h`
                  : ''}
                {routePreview.isReconsecration
                  ? ` · ${t('panels:territory.reconsecration')}`
                  : ''}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1">
                <button
                  type="button"
                  disabled={routeBusy}
                  className="rounded border border-[var(--accent)]/50 bg-[var(--accent)]/15 px-2 py-0.5 text-[10px] font-semibold text-[var(--accent)] disabled:opacity-50"
                  onClick={() => {
                    if (!routePreview) return;
                    setRouteBusy(true);
                    const key = newIdempotencyKey();
                    const op =
                      routePreview.mode === 'pilgrim'
                        ? api.pilgrimConsecrate(
                            routePreview.territoryA,
                            routePreview.territoryB,
                            key,
                          )
                        : api.scenicCreate(
                            routePreview.territoryA,
                            routePreview.territoryB,
                            key,
                          );
                    void op
                      .then((res) => {
                        if (!res.ok) {
                          alert(res.error || t('errors:actionImpossible'));
                          return;
                        }
                        setRouteMode(null);
                        setRouteAnchorId(null);
                        setRoutePreview(null);
                        setNeighborHighlight([]);
                        void qc.invalidateQueries({ queryKey: ['territory'] });
                        void qc.invalidateQueries({ queryKey: ['resources'] });
                        if (routePreview.mode === 'pilgrim') {
                          scheduleInfluenceRefresh(qc, true, { bumpMapPaint });
                        }
                      })
                      .catch((err) => alert(translateApiError(err, t, 'actionImpossible')))
                      .finally(() => setRouteBusy(false));
                  }}
                >
                  {t('panels:territory.routeConfirm')}
                </button>
                <button
                  type="button"
                  disabled={routeBusy}
                  className="rounded border border-[var(--line)] px-2 py-0.5 text-[10px] text-[var(--muted)]"
                  onClick={() => {
                    setRouteMode(null);
                    setRouteAnchorId(null);
                    setRoutePreview(null);
                    setNeighborHighlight([]);
                  }}
                >
                  {t('common:cancel')}
                </button>
              </div>
            </div>
          )}
          {(() => {
            const pilgrim = q.data?.pilgrimPaths as
              | {
                  paths?: Array<{
                    id: number;
                    territoryA: number;
                    territoryB: number;
                    pathLength: number;
                    state: string;
                    remainingSeconds?: number;
                    path?: number[];
                  }>;
                }
              | null
              | undefined;
            const scenic = q.data?.scenicRoutes as
              | {
                  routes?: Array<{
                    id: number;
                    territoryA: number;
                    territoryB: number;
                    pathLength: number;
                    state: string;
                    path?: number[];
                  }>;
                }
              | null
              | undefined;
            const paths = (pilgrim?.paths ?? []).filter((p) =>
              ['consecrating', 'active'].includes(p.state),
            );
            const routes = (scenic?.routes ?? []).filter((r) => r.state === 'active');
            if (paths.length === 0 && routes.length === 0) return null;
            return (
              <div className="mt-2 space-y-1 border-t border-[var(--line)]/60 pt-2 text-[10px]">
                {paths.map((p) => (
                  <div
                    key={`pilgrim-${p.id}`}
                    className="flex items-center justify-between gap-2 text-[var(--muted)]"
                  >
                    <button
                      type="button"
                      className="min-w-0 truncate text-left hover:text-violet-200"
                      onClick={() => setNeighborHighlight(p.path ?? [p.territoryA, p.territoryB])}
                    >
                      {t('panels:territory.pilgrimItem', {
                        a: p.territoryA,
                        b: p.territoryB,
                        length: p.pathLength,
                        state: p.state,
                      })}
                      {p.state === 'consecrating' && p.remainingSeconds
                        ? ` · ${Math.ceil(p.remainingSeconds / 3600)} h`
                        : ''}
                    </button>
                    {p.state === 'consecrating' && (
                      <button
                        type="button"
                        className="shrink-0 text-[var(--danger)]"
                        onClick={() => {
                          void api
                            .pilgrimCancel(p.id)
                            .then(() => qc.invalidateQueries({ queryKey: ['territory'] }))
                            .catch((err) =>
                              alert(translateApiError(err, t, 'actionImpossible')),
                            );
                        }}
                      >
                        {t('common:cancel')}
                      </button>
                    )}
                  </div>
                ))}
                {routes.map((r) => (
                  <div
                    key={`scenic-${r.id}`}
                    className="flex items-center justify-between gap-2 text-[var(--muted)]"
                  >
                    <button
                      type="button"
                      className="min-w-0 truncate text-left hover:text-teal-200"
                      onClick={() => setNeighborHighlight(r.path ?? [r.territoryA, r.territoryB])}
                    >
                      {t('panels:territory.scenicItem', {
                        a: r.territoryA,
                        b: r.territoryB,
                        length: r.pathLength,
                      })}
                    </button>
                    <button
                      type="button"
                      className="shrink-0 text-[var(--danger)]"
                      onClick={() => {
                        void api
                          .scenicAbandon(r.id)
                          .then(() => qc.invalidateQueries({ queryKey: ['territory'] }))
                          .catch((err) =>
                            alert(translateApiError(err, t, 'actionImpossible')),
                          );
                      }}
                    >
                      {t('panels:territory.routeAbandon')}
                    </button>
                  </div>
                ))}
              </div>
            );
          })()}
          {territoryPois.length > 0 && (
            <div className="mt-2 border-t border-[var(--line)]/60 pt-2">
              <p className="mb-1 text-[9px] uppercase tracking-wider text-[var(--muted)]/80">
                {t('panels:territory.poisAttached')}
              </p>
              <ul className="space-y-0.5">
                {territoryPois.map((f) => {
                  const p = f.properties;
                  const color = POI_COLOR[p.category] ?? 'var(--muted)';
                  const emoji = POI_EMOJI[p.category] ?? '📍';
                  const effective = governsTerritory ? poiHourlyById.get(p.id) : undefined;
                  const yieldValue =
                    effective != null ? effective : Number(p.baseYield);
                  const yieldDigits =
                    effective != null && Math.abs(effective) < 1 ? 2 : 1;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        title={
                          effective != null
                            ? t('panels:territory.poiYieldEffectiveHint', {
                                base: Number(p.baseYield).toFixed(1),
                              })
                            : undefined
                        }
                        onClick={() => {
                          setSelectedPoi(p.id);
                          setPanel('poi');
                        }}
                        className="flex w-full items-center gap-1.5 rounded-md px-1 py-0.5 text-left transition hover:bg-white/[0.04]"
                      >
                        <span className="text-[11px] leading-none" aria-hidden>
                          {emoji}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[11px] text-[var(--muted)]">
                          {p.name}
                        </span>
                        <span
                          className="shrink-0 tabular-nums text-[10px]"
                          style={{ color }}
                        >
                          {t('panels:territory.poiYield', {
                            yield: yieldValue.toFixed(yieldDigits),
                          })}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {q.isLoading && <p className="text-sm text-[var(--muted)]">{t('common:loading')}</p>}
        {terr && (
          <>
            {terr.isAggregate ? (
              <div className="rounded-2xl border border-[var(--accent)]/40 bg-[var(--accent)]/10 p-3 text-sm">
                <p className="font-medium text-[var(--accent)]">{t('panels:territory.aggregate')}</p>
                <p className="mt-1 text-[var(--muted)]">
                  {String(terr.note ?? '')}{' '}
                  {t('panels:territory.playableChildren', {
                    note: '',
                    count: Number(terr.playableChildren ?? 0),
                  })}
                </p>
              </div>
            ) : (
              <>
                {q.data?.worldWonder && (
                  <div className="mb-2.5 overflow-hidden rounded-xl border border-[var(--accent)]/45 bg-[var(--accent)]/10 sm:mb-3">
                    {q.data.worldWonder.image ? (
                      <img
                        src={q.data.worldWonder.image}
                        alt={
                          activeLocale().startsWith('fr')
                            ? q.data.worldWonder.nameFr
                            : q.data.worldWonder.nameEn || q.data.worldWonder.nameFr
                        }
                        className="h-32 w-full object-cover object-center sm:h-40"
                        style={{
                          // Monumentales / portraits : privilégier le haut (tête, sommet).
                          objectPosition:
                            q.data.worldWonder.id === 'statue_of_liberty' ||
                            q.data.worldWonder.id === 'eiffel_tower' ||
                            q.data.worldWonder.id === 'christ_redeemer' ||
                            q.data.worldWonder.id === 'atomium' ||
                            q.data.worldWonder.id === 'burj_khalifa'
                              ? 'center 22%'
                              : 'center center',
                        }}
                        loading="lazy"
                      />
                    ) : null}
                    <div className="p-2.5 sm:p-3">
                      <p className="text-[10px] uppercase tracking-wider text-[var(--accent)]">
                        {t('panels:territory.worldWonder')}
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-[var(--text)]">
                        {activeLocale().startsWith('fr')
                          ? q.data.worldWonder.nameFr
                          : q.data.worldWonder.nameEn || q.data.worldWonder.nameFr}
                      </p>
                      <p className="mt-1 text-[11px] text-[var(--muted)]">
                        {t('panels:territory.worldWonderBonus', {
                          percent: q.data.worldWonder.bonusPercent,
                          resource:
                            q.data.worldWonder.bonusType === 'all'
                              ? t('panels:territory.worldWonderBonusAll')
                              : q.data.worldWonder.bonusType === 'steel'
                                ? t('panels:territory.worldWonderSteel')
                                : q.data.worldWonder.bonusType === 'concrete'
                                  ? t('panels:territory.worldWonderConcrete')
                                  : (POI_LABEL[q.data.worldWonder.bonusType as keyof typeof POI_LABEL] ??
                                    q.data.worldWonder.bonusType),
                        })}
                      </p>
                      {q.data.worldWonder.viewerIsGovernor ? (
                        <p className="mt-1 text-[11px] text-[var(--accent)]">
                          {t('panels:territory.worldWonderYouGovern')}
                        </p>
                      ) : null}
                      <p className="mt-1 text-[10px] leading-snug text-[var(--muted)]/85">
                        {t('panels:territory.worldWonderHint')}
                      </p>
                    </div>
                  </div>
                )}

                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:gap-y-2 sm:text-sm">
                  <div>
                    <dt className="text-[10px] uppercase text-[var(--muted)]">{t('panels:territory.country')}</dt>
                    <dd className="truncate">{String(terr.country)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase text-[var(--muted)]">{t('panels:territory.region')}</dt>
                    <dd className="truncate">{String(terr.region)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase text-[var(--muted)]">{t('panels:territory.area')}</dt>
                    <dd>{terr.areaKm2 != null ? `${terr.areaKm2} km²` : t('common:notAvailable')}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase text-[var(--muted)]">{t('panels:territory.population')}</dt>
                    <dd>
                      {terr.population != null
                        ? Number(terr.population).toLocaleString(activeLocale())
                        : t('common:notAvailable')}
                      {(() => {
                        const dens =
                          terr.density != null && Number(terr.density) > 0
                            ? Number(terr.density)
                            : terr.population != null &&
                                terr.areaKm2 != null &&
                                Number(terr.areaKm2) > 0
                              ? Number(terr.population) / Number(terr.areaKm2)
                              : null;
                        if (dens == null || dens <= 0) return null;
                        return (
                          <span className="text-[var(--muted)]">
                            {' '}
                            ({Math.round(dens).toLocaleString(activeLocale())}
                            {t('common:units.inhabitantsPerSquareKilometer')})
                          </span>
                        );
                      })()}
                      {terr.populationQuality === 'estimation' && (
                        <span className="block text-[10px] text-[var(--accent)]">{t('panels:territory.spatialEstimate')}</span>
                      )}
                      {terr.populationQuality === 'official' && (
                        <span className="block text-[10px] text-[var(--muted)]">{t('panels:territory.official')}</span>
                      )}
                    </dd>
                  </div>
                </dl>

                <section className="mt-2.5 rounded-xl border border-[var(--line)] p-2.5 sm:mt-4 sm:rounded-2xl sm:p-3">
                  <h3 className="mb-1 text-xs font-semibold sm:text-sm">{t('panels:territory.influence')}</h3>
                  <p className="mb-1.5 text-[10px] text-[var(--muted)] sm:mb-2">
                    {t('panels:territory.influenceHint')}
                  </p>
                  {inf?.contested && (
                    <p className="mb-2 text-xs text-[var(--accent)]">{t('panels:territory.contested')}</p>
                  )}
                  {owners.length ? (
                    <ul className="space-y-2">
                      {owners.map((o) => {
                        const isSelf = user?.id === o.userId;
                        return (
                          <li key={o.userId} className="text-sm">
                            <div className="flex items-center gap-2">
                              {o.empireFlagUrl ? (
                                <img
                                  src={o.empireFlagUrl}
                                  alt=""
                                  title={o.empireName || o.username}
                                  className="h-3 w-[1.125rem] shrink-0 rounded-sm border border-[var(--line)] object-cover"
                                />
                              ) : (
                                <span
                                  className="h-3 w-3 shrink-0 rounded-full"
                                  style={{ background: o.empireColor }}
                                />
                              )}
                              {!isSelf && user && (
                                <button
                                  type="button"
                                  title={t('panels:territory.chatWith', { name: o.empireName || o.username })}
                                  aria-label={t('panels:territory.openChatAria', { name: o.empireName || o.username })}
                                  onClick={() => openChatWith(o.userId)}
                                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[var(--line)] text-[var(--muted)] transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
                                >
                                  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8">
                                    <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7A2.5 2.5 0 0 1 17.5 16H10l-4 3.2V16H6.5A2.5 2.5 0 0 1 4 13.5v-7Z" />
                                  </svg>
                                </button>
                              )}
                              {isSelf ? (
                                <span className="min-w-0 flex-1 truncate">
                                  {o.empireName || o.username}
                                  <span className="text-[var(--muted)]"> · {t('common:you')}</span>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => openPlayer(o)}
                                  className="min-w-0 flex-1 truncate text-left transition hover:text-[var(--accent)]"
                                  title={t('panels:territory.viewPlayer')}
                                >
                                  {o.empireName || o.username}
                                  <span className="text-[var(--muted)]"> · {o.username}</span>
                                </button>
                              )}
                              <span className="shrink-0 text-right tabular-nums">
                                <span className="block font-semibold">{Number(o.weight ?? 0).toFixed(1)}</span>
                                <span className="text-[10px] text-[var(--muted)]">{Number(o.percent ?? 0).toFixed(1)}%</span>
                              </span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-[var(--muted)]">{t('panels:territory.neutral')}</p>
                  )}
                  {inf?.intelligence?.ok && inf.intelligence.target && (() => {
                    const intel = inf.intelligence;
                    const mil = intel.military ?? { tier: 0 };
                    const eco = intel.economic ?? { tier: 0 };
                    const targetName = intel.target.empireName || intel.target.username;
                    const adv = intel.advantages ?? { military: 0, economic: 0 };
                    return (
                      <div className="mt-3 space-y-3 border-t border-[var(--line)] pt-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
                            {t('panels:territory.intelTitle', { name: targetName })}
                          </p>
                          <span className="text-[10px] text-[var(--accent)]">
                            {t('panels:territory.intelAdv', { military: adv.military, economic: adv.economic })}
                          </span>
                        </div>

                        {(mil.tier ?? 0) >= 2 && (
                          <div className="rounded-xl border border-red-400/20 bg-red-500/5 p-2.5 text-xs">
                            <p className="mb-1.5 font-semibold text-red-300">{t('panels:territory.milEspionage', { tier: mil.tier })}</p>
                            <div className="flex justify-between gap-3">
                              <span className="text-[var(--muted)]">{t('panels:territory.governorPoints')}</span>
                              <span className="font-medium tabular-nums">{Number(mil.directPoints ?? 0).toFixed(1)}</span>
                            </div>
                            {mil.tier >= 4 && (
                              <div className="mt-1 space-y-1">
                                <div className="flex justify-between gap-3"><span className="text-[var(--muted)]">{t('panels:territory.adjacency')}</span><span className="tabular-nums">+{Number(mil.adjBonus ?? 0).toFixed(1)}</span></div>
                                <div className="flex justify-between gap-3"><span className="text-[var(--muted)]">{t('panels:territory.network')}</span><span className="tabular-nums">+{Number(mil.networkBonus ?? 0).toFixed(1)}</span></div>
                                <div className="flex justify-between gap-3"><span className="text-[var(--muted)]">{t('panels:territory.alliances')}</span><span className="tabular-nums">+{Number(mil.allianceBonus ?? 0).toFixed(1)}</span></div>
                                <div className="flex justify-between gap-3"><span className="text-[var(--muted)]">{t('panels:territory.techEffects')}</span><span className="tabular-nums">+{(Number(mil.technologyBonus ?? 0) + Number(mil.temporaryBonus ?? 0)).toFixed(1)}</span></div>
                              </div>
                            )}
                          </div>
                        )}

                        {eco.tier >= 2 && eco.stocks && (
                          <div className="rounded-xl border border-blue-400/20 bg-blue-500/5 p-2.5 text-xs">
                            <p className="mb-1.5 font-semibold text-blue-300">
                              {t('panels:territory.ecoEspionage', { tier: eco.tier })}
                              {eco.approximate ? t('panels:territory.estimatesSuffix') : ''}
                            </p>
                            {Object.entries(eco.stocks).map(([resource, stock]) => (
                              <div key={resource} className="flex justify-between gap-3">
                                <span className="text-[var(--muted)]">{t(`common:resources.${resource}`, { defaultValue: resource })}</span>
                                <span className="tabular-nums">
                                  {eco.approximate ? '≈ ' : ''}{Number(stock).toLocaleString(activeLocale(), { maximumFractionDigits: 2 })}
                                  {eco.tier >= 4 && eco.hourlyRates ? ` · +${Number(eco.hourlyRates[resource] ?? 0).toLocaleString(activeLocale(), { maximumFractionDigits: 2 })}/h` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {viewer && (() => {
                    const d = Number(viewer.directPoints ?? 0);
                    const a = Number(viewer.adjBonus ?? 0);
                    const n = Number(viewer.networkBonus ?? 0);
                    const alliance = Number(viewer.allianceBonus ?? 0);
                    const tech = Number(viewer.technologyBonus ?? 0);
                    const temporary = Number(viewer.temporaryBonus ?? 0);
                    const techEffects = tech + temporary;
                    const partsSum = d + a + n + alliance + techEffects;
                    const total = Number(viewer.weight ?? partsSum);
                    // Écart éventuel : multiplicateur d’empire / bâtiments appliqué sur le poids final.
                    const other = total - partsSum;
                    return (
                      <div className="mt-3 border-t border-[var(--line)] pt-3">
                        <p className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
                          {t('panels:territory.yourWeight')}
                        </p>
                        <p className="mt-0.5 font-[family-name:var(--font-display)] text-xl tabular-nums leading-none text-[var(--accent)] sm:text-3xl">
                          {total.toFixed(1)}
                        </p>
                        <p className="mt-2 text-[11px] text-[var(--muted)]">
                          {t('panels:territory.weightBreakdown')}
                        </p>
                        <div className="mt-2 space-y-1 text-xs">
                          <div className="flex justify-between gap-3">
                            <span className="text-[var(--muted)]">{t('panels:territory.directs')}</span>
                            <span className="tabular-nums font-medium">{d}</span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-[var(--muted)]">{t('panels:territory.plusAdj')}</span>
                            <span className="tabular-nums font-medium">{a.toFixed(1)}</span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-[var(--muted)]">{t('panels:territory.plusNet')}</span>
                            <span className="tabular-nums font-medium">{n.toFixed(1)}</span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-[var(--muted)]">{t('panels:territory.plusAlliances')}</span>
                            <span className="tabular-nums font-medium">{alliance.toFixed(1)}</span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-[var(--muted)]">{t('panels:territory.techEffects')}</span>
                            <span className="tabular-nums font-medium">{techEffects.toFixed(1)}</span>
                          </div>
                          {Math.abs(other) >= 0.05 && (
                            <div className="flex justify-between gap-3">
                              <span className="text-[var(--muted)]">{t('panels:territory.empireMult')}</span>
                              <span className="tabular-nums font-medium">{other >= 0 ? '+' : ''}{other.toFixed(1)}</span>
                            </div>
                          )}
                          <div className="flex justify-between gap-3 border-t border-[var(--line)] pt-1.5 text-sm">
                            <span className="font-semibold">{t('panels:territory.equalsTotal')}</span>
                            <span className="tabular-nums font-semibold text-[var(--accent)]">
                              {total.toFixed(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </section>

              </>
            )}
          </>
        )}
      </div>

      {terr && !terr.isAggregate && martialLaw?.active && (
        <div className="shrink-0 border-t border-[var(--line)] px-3 pt-2 sm:px-4 sm:pt-3">
          <div className="rounded-xl border border-blue-300/40 bg-blue-500/10 px-3 py-2 text-center text-xs text-blue-200 sm:py-2.5 sm:text-sm">
            <span className="mr-1.5" aria-hidden="true">🛡️</span>
            {t('panels:territory.martialActiveLine', { countdown: martialCountdown })}
          </div>
        </div>
      )}
      {terr && !terr.isAggregate && user && isGovernor && (martialLaw?.level ?? 0) > 0 && (
        <div className="shrink-0 border-t border-[var(--line)] px-3 pt-2 sm:px-4 sm:pt-3">
          <button
            type="button"
            disabled={!martialLaw?.canActivate || martialLawMutation.isPending}
            onClick={() => martialLawMutation.mutate()}
            className="w-full rounded-xl border border-blue-300/50 bg-blue-500/10 px-3 py-2 text-xs font-semibold text-blue-200 transition hover:bg-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2.5 sm:text-sm"
          >
            {martialLawMutation.isPending ? t('panels:territory.activating') : martialLaw?.active ? t('panels:territory.martialActive') : t('panels:territory.activateMartial')}
          </button>
          <p className="mt-1 text-center text-[10px] text-[var(--muted)] sm:mt-1.5 sm:text-[11px]">
            {martialLaw?.active ? t('panels:territory.martialProtection', { countdown: martialCountdown }) : t('panels:territory.martialSlots', { active: martialLaw?.activeCount ?? 0, max: martialLaw?.maxActive ?? 0, hours: martialLaw?.durationHours ?? 0 })}
          </p>
          {!martialLaw?.active && !martialLaw?.canActivate && martialLaw?.worldWonderBlocked && (
            <p className="mt-1 text-center text-[10px] text-amber-300 sm:text-[11px]">{t('panels:territory.martialWonderBlocked')}</p>
          )}
          {!martialLaw?.active && !martialLaw?.canActivate && !martialLaw?.worldWonderBlocked && (martialLaw?.activeCount ?? 0) >= (martialLaw?.maxActive ?? 0) && (
            <p className="mt-1 text-center text-[10px] text-amber-300 sm:text-[11px]">{t('panels:territory.martialLimit')}</p>
          )}
        </div>
      )}
      {terr && !terr.isAggregate && user && hasInvestment && (
        <div className="shrink-0 border-t border-[var(--line)] px-3 pt-2 sm:px-4 sm:pt-3">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              disabled={pauseMutation.isPending || abandonMutation.isPending}
              onClick={() => pauseMutation.mutate(!viewerPaused)}
              className={`shrink-0 rounded-lg border px-2 py-1 text-[10px] font-semibold transition disabled:opacity-60 sm:px-2.5 sm:py-1.5 sm:text-[11px] ${viewerPaused ? 'border-emerald-400/50 bg-emerald-500/10 text-emerald-300' : 'border-amber-400/50 bg-amber-500/10 text-amber-300'}`}
            >
              {pauseMutation.isPending ? t('common:loadingShort') : viewerPaused ? t('panels:territory.unpause') : t('panels:territory.pause')}
            </button>
            {showRecoverInsteadOfAbandon ? (
              <button
                type="button"
                disabled={
                  recoverCharges < 1 ||
                  recoverMutation.isPending ||
                  pauseMutation.isPending ||
                  abandonMutation.isPending
                }
                title={
                  recoverCharges < 1
                    ? t('panels:territory.recoverEmpty')
                    : t('panels:territory.recoverHint', {
                        count: recoverCharges,
                        time: `${Math.floor(recoverRemainingSec / 60)}:${String(recoverRemainingSec % 60).padStart(2, '0')}`,
                      })
                }
                onClick={() => {
                  if (recoverCharges < 1) return;
                  const ok = window.confirm(
                    t('panels:territory.recoverConfirm', {
                      name: String(terr.name ?? ''),
                      count: recoverCharges,
                    }),
                  );
                  if (ok) recoverMutation.mutate();
                }}
                className="shrink-0 rounded-lg border border-emerald-400/50 bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold text-emerald-300 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40 sm:px-2.5 sm:py-1.5 sm:text-[11px]"
              >
                {recoverMutation.isPending
                  ? t('common:loadingShort')
                  : t('panels:territory.recover', { count: recoverCharges })}
              </button>
            ) : graceActive ? (
              <button
                type="button"
                disabled={abandonMutation.isPending || pauseMutation.isPending || recoverMutation.isPending}
                onClick={() => {
                  const count = Number(viewer?.investmentCount ?? 1) || 1;
                  const ok = window.confirm(
                    t('panels:territory.abandonConfirm', { name: String(terr.name ?? ''), count }),
                  );
                  if (ok) abandonMutation.mutate();
                }}
                className="shrink-0 rounded-lg border border-[var(--danger)]/50 bg-[var(--danger)]/10 px-2 py-1 text-[10px] font-semibold text-[var(--danger)] transition hover:bg-[var(--danger)]/20 disabled:opacity-60 sm:px-2.5 sm:py-1.5 sm:text-[11px]"
              >
                {abandonMutation.isPending ? t('common:loadingShort') : t('panels:territory.abandon')}
              </button>
            ) : null}
            {!foreignGovernor && (
              <button
                type="button"
                onClick={() => openBuildings('territory')}
                className="min-w-0 flex-1 rounded-lg border border-[var(--line)] bg-white/5 px-2 py-1 text-[10px] font-semibold text-[var(--ink)] transition hover:border-[var(--accent)] hover:text-[var(--accent)] sm:px-2.5 sm:py-1.5 sm:text-[11px]"
              >
                {t('panels:territory.buildings')}
              </button>
            )}
          </div>
          {viewerPaused && (
            <p className="mt-1.5 text-center text-[10px] text-[var(--muted)] sm:mt-2 sm:text-[11px]">
              {t('panels:territory.pauseExplain')}
            </p>
          )}
        </div>
      )}
      {terr && !terr.isAggregate && user && foreignGovernor && (
        <div className="shrink-0 border-t border-[var(--line)] px-3 pt-2 sm:px-4 sm:pt-3">
          <button
            type="button"
            onClick={() => openBuildings('territory')}
            className="w-full rounded-lg border border-[var(--line)] bg-white/5 px-3 py-1.5 text-xs font-semibold text-[var(--ink)] transition hover:border-[var(--accent)] hover:text-[var(--accent)] sm:py-2 sm:text-sm"
          >
            {t('panels:territory.buildings')}
          </button>
        </div>
      )}
      {terr && !terr.isAggregate && user && !viewerPaused && !blockedByAlly && !blockedByAdjacentGov && !blockedByMartialLaw && (
        <div className="shrink-0 border-t border-[var(--line)] px-3 py-2.5 sm:p-4 sm:pt-3">
          <button
            type="button"
            disabled={investDisabled}
            title={investTitle}
            onClick={() => void invest()}
            className="w-full rounded-xl bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[#1a1408] disabled:cursor-not-allowed disabled:opacity-45 sm:py-3"
          >
            {conquering ? t('panels:territory.investing') : t('panels:territory.invest')}
            {viewer?.nextPoints != null ? ` · +${String(viewer.nextPoints)}` : ''}
            <span className="ml-1.5 hidden text-[10px] font-medium opacity-70 sm:inline">(I)</span>
          </button>
          {noCharges && (
            <p className="mt-1.5 text-center text-[10px] text-[var(--muted)] sm:mt-2 sm:text-[11px]">{t('panels:territory.notEnoughCharges')}</p>
          )}
          {!noCharges && blockedByZones && zonesInfo && (
            <p className="mt-1.5 text-center text-[10px] leading-snug text-[var(--danger)] sm:mt-2 sm:text-[11px]">
              {t('panels:territory.zoneLimit', { count: zonesInfo.count, max: zonesInfo.max })}
            </p>
          )}
        </div>
      )}
      {terr && !terr.isAggregate && user && blockedByMartialLaw && (
        <div className="shrink-0 border-t border-[var(--line)] px-3 py-2.5 sm:p-4 sm:pt-3">
          <p className="rounded-xl border border-blue-300/40 bg-blue-500/10 px-3 py-2 text-[11px] text-blue-200 sm:text-xs">
            {t('panels:territory.martialProtected', { countdown: martialCountdown })}
          </p>
        </div>
      )}
      {terr && !terr.isAggregate && user && blockedByAlly && allyGovernor && (
        <div className="shrink-0 border-t border-[var(--line)] px-3 py-2.5 sm:p-4 sm:pt-3">
          <p className="rounded-xl border border-[var(--accent)]/40 bg-[var(--accent)]/10 px-3 py-2 text-[11px] text-[var(--accent)] sm:text-xs">
            {t('panels:territory.blockedAlly', { name: allyGovernor.empireName || allyGovernor.username })}
          </p>
        </div>
      )}
      {terr && !terr.isAggregate && user && blockedByAdjacentGov && (
        <div className="shrink-0 border-t border-[var(--line)] px-3 py-2.5 sm:p-4 sm:pt-3">
          <p className="rounded-xl border border-[var(--danger)]/35 bg-[var(--danger)]/10 px-3 py-2 text-[11px] text-[var(--danger)] sm:text-xs">
            {t('panels:territory.blockedAdjacentGovernor')}
          </p>
        </div>
      )}
    </aside>
  );
}
